import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormRenderer } from '../components/FormRenderer';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { FormStyles } from '../components/FormStyles';
import { BindingError } from '../components/BindingError';
import {
  LoadingState,
  ErrorState,
  SuccessState,
  RedirectState,
  NoDataState,
} from '../components/FormStateComponents';
import { useFormFlow } from '../hooks/useFormFlow';
import {
  generateCSSVariables,
  resolveTheme,
  LanguageSelection,
  generateAdvancedCSSVariables,
} from '@dynamic-flow/ui';
import {
  validateUrlBindings,
  generateBindingErrorMessage,
} from '../utils/bindingValidation';

export const PublicFormView: React.FC = () => {
  const { flowId, lang = 'en' } = useParams<{
    flowId: string;
    lang?: string;
  }>();
  const navigate = useNavigate();

  // Use the custom hook for flow loading
  const { flow, loading, error, reload } = useFormFlow(
    flowId,
    'published',
    lang
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
    lang
  );

  const handleSubmitSuccess = () => {
    setIsSubmitted(true);

    // If there's a redirect URL, navigate to it
    if (flow?.settings.redirectUrl) {
      setTimeout(() => {
        window.location.href = flow.settings.redirectUrl!;
      }, 1000);
    }
  };

  // Loading state
  if (loading) {
    return <LoadingState flow={flow} flowId={flowId} />;
  }

  // Error state
  if (error) {
    console.log('error', error);
    return <ErrorState error={error} />;
  }

  // Success state (after submission)
  if (isSubmitted && !flow?.settings.redirectUrl && flow) {
    return (
      <SuccessState
        flow={flow}
        onReset={() => {
          setIsSubmitted(false);
          window.location.reload();
        }}
      />
    );
  }

  // Redirect to thank you page if configured
  if (isSubmitted && flow?.settings.redirectUrl) {
    return <RedirectState />;
  }

  // No data state
  if (!flow) {
    return <NoDataState />;
  }

  // Validate URL bindings
  const bindingValidation = validateUrlBindings(flow);
  if (!bindingValidation.isValid) {
    const errorMessage = generateBindingErrorMessage(bindingValidation);
    return (
      <BindingError flow={flow} message={errorMessage} isPreview={false} />
    );
  }

  // Get the theme for this flow (advanced theme overrides basic theme)
  const theme = resolveTheme(flow.theme);
  const themeCSS = generateCSSVariables(theme);
  const advancedThemeCSS = generateAdvancedCSSVariables(
    flow.theme?.advanced ?? {}
  );

  const mergedCSS = `${themeCSS}\n${advancedThemeCSS}`;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: theme.colors.background.default }}
    >
      {/* Apply theme CSS variables */}
      <style dangerouslySetInnerHTML={{ __html: mergedCSS }} />

      {/* Use shared form styles component */}
      <FormStyles />

      <div className="max-w-md mx-auto flex flex-1 min-h-0 flex-col gap-2 w-full p-4">
        <ErrorBoundary>
          <div className="self-end shrink-0">
            <LanguageSelection
              loadFlow={reload}
              value={selectedLanguage}
              onValueChange={(code) => {
                setSelectedLanguage(code);
                const currentSearch = window.location.search;
                const targetPath = `/${code}/public/${flowId}${currentSearch}`;
                navigate(targetPath, { replace: true });
              }}
            />
          </div>
          <div className="flex-1 flex flex-col min-h-0 w-full">
            <FormRenderer
              isPreview={false}
              flow={flow}
              onSubmit={handleSubmitSuccess}
            />
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
};
